"use client";

import { useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { useMatches } from "@/hooks/useMatches";
import { LIVE_STATUSES } from "@/contants";
import LobbyKbvMatchMinute from "@/components/LobbyKbvMatchMinute";
import LobbyOddRow from "@/components/LobbyMatch/LobbyOddRow";
import LobbyOtherBetsDropdown from "@/components/LobbyOtherBetsDropdown/LobbyOtherBetsDropdown";
import ViuGoTipsAiModal from "@/components/ViuGoTipsAiModal";
import { formatCorrectScoresForGrid, splitCorrectScoresListToSections } from "@/utils/matchInfo";
import { formatUnixToVNDate } from "@/utils/format";
import viugoTipsAiSparkle from "@/assets/images/viugo/kbv-v16-tips-ai-sparkle.svg";
import ballIcon from "@/assets/images/events/goal.svg";
import ICON_ARROW_UP from "@/assets/images/arrow-up.svg";
import "./index.css";

export default function LobbyMatchV28({ match, isOdd }) {
  const [showOther, setShowOther] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const { getMatchOdds, getMatchInsight } = useMatches();

  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const { currentHomeScore: hS, currentAwayScore: aS } = useMatchData(
    liveScore,
    match,
  );

  const homeScore =
    liveScore?.homeScore ??
    match?.homeScore ??
    match?.home_score ??
    match?.score?.home ??
    hS;
  const awayScore =
    liveScore?.awayScore ??
    match?.awayScore ??
    match?.away_score ??
    match?.score?.away ??
    aS;

  const isHomeWin = Number(homeScore) > Number(awayScore);
  const isAwayWin = Number(homeScore) < Number(awayScore);

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const odds = useMatchOdds(match, filterOdds, companyIdOdds);
  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = odds;

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match?.status ?? 0);
  const isLive = LIVE_STATUSES.includes(statusNum);

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;

  const formatDateShort = (matchTime) => {
    if (!matchTime) return "";
    const d = new Date(matchTime * 1000);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const formatTime = (matchTime) => {
    if (!matchTime) return "";
    const d = new Date(matchTime * 1000);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const leagueName =
    match?.league?.name || match?.leagueName || match?.name || "-";
  const hasSport = !!homeHandicapHalfOdds || !!awayHandicapHalfOdds;

  const hasFtHandicap = homeHandicapOdds || awayHandicapOdds;
  const hasFtOU = overOdds || underOdds;
  const hasFt1x2 = homeEuropeOdds || awayEuropeOdds || instantDrawOdds;
  const hasHtHandicap = homeHandicapHalfOdds || awayHandicapHalfOdds;
  const hasHtOU = overHalfOdds || underHalfOdds;
  const hasHt1x2 = homeEuropeHalfOdds || awayEuropeHalfOdds || drawHalfOdds;
  const showHalfRow = hasSport || hasHt1x2;

  const handleOtherOddsClick = (e) => {
    e.stopPropagation();
    setShowOther((prev) => !prev);
    if (!showOther && !otherOddsData) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const handleOpenTipsAiModal = async (e) => {
    e?.stopPropagation?.();
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight || !match?.id) return;

    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match.id);
      if (insightResponse?.success && insightResponse?.data) {
        setTipsAiInsightData(insightResponse.data);
      }
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  const goalOddEvenOdds = otherOddsData?.goalOddEvenOdds ?? [];
  const correctScoresListFull = otherOddsData?.correctScoresOdds ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds) : [];
  const correctScoresListHalf = otherOddsData?.correctScoresHalfOdds ? formatCorrectScoresForGrid(otherOddsData.correctScoresHalfOdds) : [];
  const correctScoresFull = splitCorrectScoresListToSections(correctScoresListFull);
  const correctScoresHalf = splitCorrectScoresListToSections(correctScoresListHalf);
  const totalCornersOdds = Array.isArray(otherOddsData?.totalCornersOdds) ? otherOddsData.totalCornersOdds : [];
  const totalCornersHalfOdds = Array.isArray(otherOddsData?.totalCornersHalfOdds) ? otherOddsData.totalCornersHalfOdds : [];
  const hasOtherData = goalOddEvenOdds?.length > 0 || correctScoresListFull.length > 0 || totalCornersOdds.length > 0 || correctScoresListHalf.length > 0 || totalCornersHalfOdds.length > 0;

  const { date: matchDateStr, gio: matchTimeStr } = formatUnixToVNDate(match?.matchTime);

  return (
    <div className={`flex flex-col overflow-hidden transition-all ${isOdd ? "bg-[var(--theme-foreground-60,#141414d9)]" : "bg-[var(--theme-highlight-40,#1c1c1cb3)]"}`}>
      <div className="hidden md:grid items-center cursor-pointer text-[var(--theme-text)] text-xsm leading-5 px-2 min-h-[80px] grid-cols-[minmax(0,24fr)_minmax(0,10fr)_minmax(0,25fr)_25px_minmax(0,19.5fr)_minmax(0,19.5fr)_minmax(0,19.5fr)] gap-2">
        <div className="kbv-wr-league-name min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[var(--theme-text)]">
          {leagueName}
        </div>

        <div className="flex flex-col items-center min-w-0 text-[var(--theme-text)]">
          {isLive && (
            <div className="flex items-center gap-[3px] bg-[var(--theme-primary)] text-[var(--theme-on-primary)] rounded-[2px] text-xxs font-semibold h-[18px] px-1 mb-0.5 justify-center min-w-[50px]">
              <span className="kbv-wr-live-dot" />
              Live
            </div>
          )}
          <div className="font-bold text-[13px]">
            <LobbyKbvMatchMinute
              liveScore={liveScore}
              status={statusNum}
              matchTime={match?.matchTime}
              halfStartTime={match?.halfStartTime}
            />
          </div>
          {isLive ? (
            <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-0.5 rounded-full bg-[var(--theme-primary)]/10 border border-[var(--theme-primary)]/20 shadow-sm">
              <img src={ballIcon} alt="ball" className="w-3 h-3" />
              <span className="font-bold text-[14px] text-[var(--theme-primary)] tabular-nums leading-none">
                {homeScore} - {awayScore}
              </span>
            </div>
          ) : (
            <div className="text-sm mt-1 text-[var(--theme-text-muted)] font-medium">
              {formatDateShort(match?.matchTime)}
            </div>
          )}
        </div>

        <div className="flex items-center min-w-0 flex-1 h-full">
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <div className="flex items-center gap-1.5 font-bold min-w-0">
              {homeLogo && (
                <img
                  src={homeLogo}
                  alt=""
                  className="w-[22px] h-[22px] object-contain shrink-0"
                  loading="lazy"
                />
              )}
              <span
                className={`overflow-hidden text-ellipsis whitespace-nowrap ${isHomeWin ? "!text-[var(--theme-primary)]" : "text-[var(--theme-text)]"}`}
              >
                {match?.homeName || match?.home_name || "-"}
              </span>
            </div>
            <div className="flex items-center gap-1.5 font-bold min-w-0">
              {awayLogo && (
                <img
                  src={awayLogo}
                  alt=""
                  className="w-[22px] h-[22px] object-contain shrink-0"
                  loading="lazy"
                />
              )}
              <span
                className={`overflow-hidden text-ellipsis whitespace-nowrap ${isAwayWin ? "!text-[var(--theme-primary)]" : "text-[var(--theme-text)]"}`}
              >
                {match?.awayName || match?.away_name || "-"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1 min-w-0 text-[10px] font-bold text-[var(--theme-text-muted)] opacity-60">
          <div className="h-7 flex items-center justify-center">FT</div>
          {showHalfRow && <div className="h-7 flex items-center justify-center">HT</div>}
        </div>

        <div className="flex items-center min-w-[150px] border-r border-[var(--theme-border)]/10 pr-4">
          <div className="flex flex-col gap-1 flex-1 pr-4">
            <LobbyOddRow
              leftValue={homeHandicapOdds}
              leftTrend={homeHandicapTrend}
              centerValue={handicapValue}
              rightValue={awayHandicapOdds}
              rightTrend={awayHandicapTrend}
              empty={!hasFtHandicap}
            />
            {showHalfRow && (
              <LobbyOddRow
                leftValue={homeHandicapHalfOdds}
                leftTrend={homeHandicapHalfTrend}
                centerValue={handicapHalfValue}
                rightValue={awayHandicapHalfOdds}
                rightTrend={awayHandicapHalfTrend}
                empty={!hasHtHandicap}
              />
            )}
          </div>
        </div>

        <div className="flex items-center min-w-[150px] border-r border-[var(--theme-border)]/10 pr-4">
          <div className="flex flex-col gap-1 flex-1 pr-4">
            <LobbyOddRow
              leftValue={overOdds}
              leftTrend={overTrend}
              centerValue={overUnderValue}
              rightValue={underOdds}
              rightTrend={underTrend}
              empty={!hasFtOU}
            />
            {showHalfRow && (
              <LobbyOddRow
                leftValue={overHalfOdds}
                leftTrend={overHalfTrend}
                centerValue={overUnderHalfValue}
                rightValue={underHalfOdds}
                rightTrend={underHalfTrend}
                empty={!hasHtOU}
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1 min-w-0">
          <LobbyOddRow
            leftValue={homeEuropeOdds}
            leftTrend={homeEuropeTrend}
            centerValue={instantDrawOdds}
            centerTrend={drawTrend}
            rightValue={awayEuropeOdds}
            rightTrend={awayEuropeTrend}
            empty={!hasFt1x2}
          />
          {showHalfRow && (
            <LobbyOddRow
              leftValue={homeEuropeHalfOdds}
              leftTrend={homeEuropeHalfTrend}
              centerValue={drawHalfOdds}
              centerTrend={drawHalfTrend}
              rightValue={awayEuropeHalfOdds}
              rightTrend={awayEuropeHalfTrend}
              empty={!hasHt1x2}
            />
          )}
        </div>
      </div>

      <div className="hidden md:flex justify-end items-center gap-2 px-2 pb-2 pt-1">
        <button
          type="button"
          onClick={handleOpenTipsAiModal}
          className="kbv-v28-tips-ai-btn flex items-center gap-1.5 rounded-[4px] px-3 py-1 text-[var(--theme-on-primary)]"
        >
          <img src={viugoTipsAiSparkle} alt="" className="kbv-v28-tips-ai-sparkle h-3.5 w-3.5 shrink-0" />
          <span className="text-[11px] font-bold uppercase tracking-tight">Tips AI</span>
        </button>
        <div
          onClick={handleOtherOddsClick}
          className="flex items-center gap-1.5 bg-[var(--theme-primary)]/10 px-3 py-1 rounded border border-[var(--theme-primary)]/20 shadow-sm hover:scale-105 transition-all cursor-pointer"
        >
          {!showOther && (
            <span className="text-[12px] font-bold text-[var(--theme-primary)]">
              {match?.countOtherOdds || 0}
            </span>
          )}
          <span className="text-[11px] font-bold text-[var(--theme-text)] uppercase tracking-wider">
            {showOther ? "Thu gọn" : "Kèo khác"}
          </span>
          <img
            src={ICON_ARROW_UP}
            alt=""
            className={`block w-3 h-3 text-[var(--theme-primary)] transition-all ${showOther ? "rotate-180" : ""}`}
            style={{ filter: "invert(48%) sepia(79%) saturate(2476%) hue-rotate(331deg) brightness(98%) contrast(97%)" }}
          />
        </div>
      </div>

      <div className="md:hidden flex flex-col p-0.5 bg-[var(--theme-foreground)] border-t border-[var(--theme-border)]/5 overflow-hidden">
        <div className="flex items-center justify-between bg-[var(--theme-bg)] px-2 py-4 h-[75px]">
          <div className="flex flex-col items-center w-[100px] text-center gap-2">
            <img
              src={homeLogo || ballIcon}
              alt="home"
              className="w-[30px] h-[30px] object-contain"
            />
            <div className={`text-[13px] font-bold break-words w-full ${isHomeWin ? "text-[var(--theme-primary)]" : "text-[var(--theme-text)]"}`}>
              {match?.homeName || match?.home_name || "-"}
            </div>
          </div>

          <div className="flex flex-col items-center flex-1 text-center font-medium gap-1">
            <div className="kbv-wr-league-name text-[12px] text-[var(--theme-text-muted)] line-clamp-1 w-full max-w-[150px]">
              {leagueName}
            </div>
            <div className="text-[14px] font-bold">
              {isLive ? (
                <div className="flex flex-col items-center gap-1">
                  <div className="text-[13px] font-bold">
                    <LobbyKbvMatchMinute
                      liveScore={liveScore}
                      status={statusNum}
                      matchTime={match?.matchTime}
                      halfStartTime={match?.halfStartTime}
                    />
                  </div>
                  <div className="text-[16px] text-[var(--theme-primary)] font-bold">
                    {homeScore} - {awayScore}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <span className="text-[14px] font-bold">{formatTime(match?.matchTime)}</span>
                  <span className="text-[12px] text-[var(--theme-text-muted)] font-normal">{formatDateShort(match?.matchTime)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-center w-[100px] text-center gap-2">
            <img
              src={awayLogo || ballIcon}
              alt="away"
              className="w-[30px] h-[30px] object-contain"
            />
            <div className={`text-[13px] font-bold break-words w-full ${isAwayWin ? "text-[var(--theme-primary)]" : "text-[var(--theme-text)]"}`}>
              {match?.awayName || match?.away_name || "-"}
            </div>
          </div>
        </div>

        <div className="flex flex-col p-2 gap-2">
          <div className="flex items-center justify-between text-[11px] text-[var(--theme-text-muted)] px-1 font-medium">
            <div className="w-[90px] text-center uppercase">Kèo chấp</div>
            <div className="w-[90px] text-center uppercase">Tài xỉu</div>
            <div className="w-[90px] text-center uppercase">1x2</div>
          </div>

          <div className="flex items-center justify-between h-[30px] gap-1">
            <div className="text-[10px] font-bold text-[var(--theme-text-muted)] opacity-60 w-5 flex shrink-0 items-center justify-center">FT</div>
            <div className="flex-1 flex items-center justify-between gap-1">
              <div className="flex-1 flex items-center border-r border-[var(--theme-border)]/10 pr-1">
                <div className="flex-1">
                  <LobbyOddRow
                    leftValue={awayHandicapOdds}
                    leftTrend={awayHandicapTrend}
                    centerValue={handicapValue}
                    rightValue={homeHandicapOdds}
                    rightTrend={homeHandicapTrend}
                    leftColor="kbv-wr-odd-red"
                    centerColor="kbv-wr-odd-blue"
                    rightColor="kbv-wr-odd-green"
                    isMobile
                  />
                </div>
              </div>
              <div className="flex-1 flex items-center border-r border-[var(--theme-border)]/10 pr-1">
                <div className="flex-1">
                  <LobbyOddRow
                    leftValue={overOdds}
                    leftTrend={overTrend}
                    centerValue={overUnderValue}
                    rightValue={underOdds}
                    rightTrend={underTrend}
                    leftColor="kbv-wr-odd-green"
                    centerColor="kbv-wr-odd-blue"
                    rightColor="kbv-wr-odd-red"
                    isMobile
                  />
                </div>
              </div>
              <div className="flex-1">
                <LobbyOddRow
                  leftValue={homeEuropeOdds}
                  leftTrend={homeEuropeTrend}
                  centerValue={instantDrawOdds}
                  centerTrend={drawTrend}
                  rightValue={awayEuropeOdds}
                  rightTrend={awayEuropeTrend}
                  leftColor="kbv-wr-odd-green"
                  centerColor="kbv-wr-odd-blue"
                  rightColor="kbv-wr-odd-red"
                  isMobile
                />
              </div>
            </div>
          </div>

          {showHalfRow && (
            <div className="flex items-center justify-between h-[30px] gap-1">
              <div className="text-[10px] font-bold text-[var(--theme-text-muted)] opacity-60 w-5 flex shrink-0 items-center justify-center">HT</div>
              <div className="flex-1 flex items-center justify-between gap-1">
                <div className="flex-1 flex items-center border-r border-[var(--theme-border)]/10 pr-1">
                  <div className="flex-1 pr-1">
                    <LobbyOddRow
                      leftValue={awayHandicapHalfOdds}
                      leftTrend={awayHandicapHalfTrend}
                      centerValue={handicapHalfValue}
                      rightValue={homeHandicapHalfOdds}
                      rightTrend={homeHandicapHalfTrend}
                      leftColor="kbv-wr-odd-red"
                      centerColor="kbv-wr-odd-blue"
                      rightColor="kbv-wr-odd-green"
                      isMobile
                    />
                  </div>
                </div>
                <div className="flex-1 flex items-center border-r border-[var(--theme-border)]/10 pr-1">
                  <div className="flex-1 pr-1">
                    <LobbyOddRow
                      leftValue={overHalfOdds}
                      leftTrend={overHalfTrend}
                      centerValue={overUnderHalfValue}
                      rightValue={underHalfOdds}
                      rightTrend={underHalfTrend}
                      leftColor="kbv-wr-odd-green"
                      centerColor="kbv-wr-odd-blue"
                      rightColor="kbv-wr-odd-red"
                      isMobile
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <LobbyOddRow
                    leftValue={homeEuropeHalfOdds}
                    leftTrend={homeEuropeHalfTrend}
                    centerValue={drawHalfOdds}
                    centerTrend={drawHalfTrend}
                    rightValue={awayEuropeHalfOdds}
                    rightTrend={awayEuropeHalfTrend}
                    leftColor="kbv-wr-odd-green"
                    centerColor="kbv-wr-odd-blue"
                    rightColor="kbv-wr-odd-red"
                    isMobile
                  />
                </div>
              </div>
            </div>
          )}

          <div className="md:hidden flex items-center justify-end gap-2 py-1 mt-1 border-t border-[var(--theme-border)]/5 pr-3">
            <button
              type="button"
              onClick={handleOpenTipsAiModal}
              className="kbv-v28-tips-ai-btn flex items-center gap-1.5 rounded-full px-3 py-1 text-[var(--theme-on-primary)]"
            >
              <img src={viugoTipsAiSparkle} alt="" className="kbv-v28-tips-ai-sparkle h-3.5 w-3.5 shrink-0" />
              <span className="text-[11px] font-bold uppercase tracking-tight">Tips AI</span>
            </button>
            {match?.countOtherOdds > 1 && (
              <button
                onClick={handleOtherOddsClick}
                className="flex items-center gap-1.5 px-3 py-1 bg-[var(--theme-highlight)]/50 rounded-full hover:bg-[var(--theme-highlight)] transition-colors"
              >
                <span className="text-[11px] font-bold text-[var(--theme-text)] opacity-70">
                  {match?.countOtherOdds || 0} kèo khác
                </span>
                <img src={ICON_ARROW_UP} alt="" className={`block w-2.5 h-2.5 opacity-60 transition-transform ${showOther ? "rotate-180" : ""}`} />
              </button>
            )}
          </div>
        </div>
      </div>

      {showOther && (
        <div className="kbv-v28-other-bets">
          <LobbyOtherBetsDropdown
            loadingOtherOdds={loadingOtherOdds}
            otherOddsData={otherOddsData}
            hasOtherData={hasOtherData}
            correctScoresListFull={correctScoresListFull}
            correctScoresFull={correctScoresFull}
            totalCornersOdds={totalCornersOdds}
            odds={odds}
            showOther={showOther}
          />
        </div>
      )}

      {openTipsAiModal &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[99999998] bg-black/50"
            style={{ backdropFilter: "blur(4px)" }}
            onClick={() => setOpenTipsAiModal(false)}
            aria-hidden="true"
          />,
          document.body,
        )}

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{
          ...formatUnixToVNDate(match?.matchTime),
          isLive,
        }}
        statsInfo={{
          currentHomeScore: homeScore,
          currentAwayScore: awayScore,
          homeYellow: null,
          homeRed: null,
          awayYellow: null,
          awayRed: null,
          currentHomeCorner: null,
          currentAwayCorner: null,
        }}
        oddsInfo={{
          handicapValue,
          homeHandicapOdds,
          awayHandicapOdds,
          overUnderValue,
          overOdds,
          underOdds,
          homeEuropeOdds,
          awayEuropeOdds,
          instantDrawOdds,
          handicapHalfValue,
          homeHandicapHalfOdds,
          awayHandicapHalfOdds,
        }}
        leagueName={match?.league?.name || match?.leagueName || leagueName}
        leagueLogo={match?.league?.logo || match?.leagueLogo || ""}
        homeName={match?.homeName || match?.home_name}
        homeLogo={homeLogo || ""}
        awayName={match?.awayName || match?.away_name}
        awayLogo={awayLogo || ""}
        matchDate={matchDateStr}
        matchTime={matchTimeStr}
        matchTimestamp={match?.matchTime}
        headerScore={`${homeScore ?? 0} - ${awayScore ?? 0}`}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={!isLive ? "Sắp diễn ra" : "Trực tiếp"}
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />
    </div>
  );
}
